/**
 * Shared helpers and types for report filter → URL-query conversion.
 *
 * Every conversation report builds the same `URLSearchParams` from the same
 * filter shape (timezone + date+time window + comma-separated campaign IDs).
 * Centralising it here keeps the hook files focused on state and fetch, and
 * guarantees the report and timeline endpoints receive identical parameters.
 */

/** The filter shape every conversation-report hook accepts from its drawer. */
export interface CampaignReportFilters {
  timezone: string
  /** '30m' | '60m' | '1d' — mirrors Agent Performance's interval selector. */
  interval: string
  startDate: string
  endDate: string
  /** HH:MM, optional — defaults to the whole day on the backend. */
  startTime: string
  endTime: string
  campaignIds: string[]
}

/**
 * Build the query-string for a report/timeline fetch. Empty time inputs mean
 * the whole day — the backend applies that default, so an untouched field is
 * simply omitted rather than sent as an empty string.
 */
export function buildReportParams(filters: CampaignReportFilters): URLSearchParams {
  const params = new URLSearchParams({
    timezone: filters.timezone,
    interval: filters.interval,
    startDate: filters.startDate,
    endDate: filters.endDate,
    campaignIds: filters.campaignIds.join(','),
  })
  if (filters.startTime) params.set('startTime', filters.startTime)
  if (filters.endTime) params.set('endTime', filters.endTime)
  return params
}
